/**
 * Client for the Logiblocks AI Builder (backend/logiblocks_agent).
 *
 * `serialized_data` on every call is a base64-encoded LBWorkflow protobuf —
 * see @/pages/Logiblocks/serialization.ts's reactFlowToLBWorkflow +
 * lbWorkflowToBase64. The chat/validate_graph split mirrors the backend's
 * two-gate design: chat() runs Gate 1 (structural) internally and returns a
 * plan; the frontend folds that plan onto the canvas graph itself (via
 * agent/applyGraphOps.ts) and posts the result to validateGraph() for Gate 2
 * (the authoritative validate_workflow check) before ever committing it.
 */
import axios, { type AxiosResponse } from 'axios';
import { guardPreviewMutations } from '@/common/interceptor/interceptor';
import interceptor, { baseUrl } from '../common/interceptor';
import { APIPaths } from '../common/constants';
import { ApiResponse } from '@/apis/types';
import type { TextTemplateChunk } from '@/proto/logiblocks/logiblocks';
import {
  browserAuthoringCapabilities,
  type AuthoringSchema,
} from '@/pages/Logiblocks/agent/authoringSchema';

export type AgentPlanStatus = 'plan_proposed' | 'clarifying_question' | 'error';
export type AgentDraftProtocolVersion = 1 | 2;
/** Historical/retired metadata may retain 1; executable readers require 2. */
export type AgentGraphOpsVersion = 1 | 2;

export type AgentDraftPhase =
  | 'planning_topology'
  | 'planning_edit'
  | 'topology_segment_issued'
  | 'edit_segment_issued'
  | 'configuring'
  | 'configuration_segment_issued'
  | 'awaiting_answer'
  | 'complete';

export interface AgentConfigurationNodeState {
  node_id: string;
  status:
    | 'awaiting_configuration'
    | 'awaiting_dependency'
    | 'awaiting_answer'
    | 'configured';
  pending_field_names?: string[];
}

export interface AgentQuestionChoice {
  value: string;
  label: string;
  disabled?: boolean;
  status?: string | null;
  color?: string | null;
  can_setup?: boolean;
  recipient_kind?: 'team' | 'user';
  team_id?: string;
  team_label?: string;
  user_id?: string;
  email?: string | null;
}

export interface AgentStructuredQuestion {
  question_id: string;
  question_index?: number;
  question_count?: number;
  kind: 'single_select' | 'multi_select' | 'team_picker' | 'text';
  /** Server-owned presentation hint from the catalog field's value domain. */
  input_type?: 'phone';
  prompt: string;
  choices: AgentQuestionChoice[];
  allow_other: boolean;
  min_selections: number;
  max_selections: number;
  default_values?: string[];
  resource_kind?: string | null;
  resource_filter?: { channel_type?: string } | null;
  node_id?: string | null;
  node_ref?: string | null;
  field_name?: string | null;
  supports_dynamic_variables?: boolean;
  variable_context_ops?: unknown[];
  variable_context_graph_ops_version?: AgentGraphOpsVersion;
}

export interface AgentAttachmentMeta {
  filename: string;
  content_type: string;
  size: number;
}

export interface AgentConversationTurn {
  role: 'user' | 'assistant';
  content: string;
  plan?: {
    summary: string;
    ops: unknown[];
    graph_ops_version?: AgentGraphOpsVersion;
  } | null;
  plan_status?: AgentPlanStatus | null;
  tool_use_id?: string | null;
  /** v1: one image/PDF attachment per user turn, metadata only -- the raw
   * bytes are never persisted server-side, see services/attachments.py. */
  attachment?: AgentAttachmentMeta | null;
  answer_to_question?: {
    question_id: string;
    values: string[];
    template_chunks?: TextTemplateChunk[];
    display_labels: string[];
    prompt?: string;
  };
  v2_outcome?: AgentChatTurnResponse | null;
}

export interface StartAgentSessionResponse {
  graph_ops_version?: AgentGraphOpsVersion;
  catalog_hash?: string;
  authoring_schema?: AuthoringSchema;
  session_id: string;
  session_state_version?: number;
  supported_draft_protocol_versions?: number[];
  supported_configuration_execution_modes?: Array<
    'serial' | 'prepare_then_finalize'
  >;
  active_draft?: {
    graph_ops_version?: AgentGraphOpsVersion;
    draft_protocol_version: AgentDraftProtocolVersion;
    draft_id: string;
    segment_id?: string | null;
    draft_status: AgentDraftPhase | 'drafting';
    phase?: AgentDraftPhase;
    question?: AgentStructuredQuestion | null;
    workflow_graph_revision?: number;
    graph_fingerprint?: string;
    terminal_status?:
      | 'cancelled'
      | 'expired'
      | 'recovery_conflict'
      | 'workflow_archived'
      | 'incompatible'
      | 'failed_manual_recovery'
      | null;
    bounded_failure_code?: string | null;
    candidate_attempt_id?: string | null;
    candidate_attempt_number?: number;
    configuration_node_states?: AgentConfigurationNodeState[];
    discard_available?: boolean;
    discard_expires_at?: string;
    topology_clarification?: {
      id: string;
      question: string;
      round: number;
    } | null;
    configuration_execution_mode?: 'serial' | 'prepare_then_finalize';
    preparation_status?: 'not_started' | 'running' | 'ready' | 'failed';
  } | null;
  conversation_history: AgentConversationTurn[];
}

export interface AgentChatTurnResponse {
  graph_ops_version?: AgentGraphOpsVersion;
  catalog_hash?: string;
  authoring_schema?: AuthoringSchema;
  kind:
    | AgentPlanStatus
    | 'draft_segment'
    | 'segment_acknowledged'
    | 'draft_cancelled'
    | 'draft_discarded'
    | 'answer_acknowledged'
    | 'already_acknowledged'
    | 'question_pending'
    | 'configuration_prepared';
  response_version?: 2;
  draft_protocol_version?: AgentDraftProtocolVersion;
  draft_id?: string;
  session_state_version?: number;
  turn_id?: string;
  segment_id?: string;
  intent_summary?: string | null;
  presentation?: {
    intent_summary: string;
    node_ids: string[];
  };
  draft_status?: AgentDraftPhase | 'drafting';
  phase?: AgentDraftPhase;
  question?: AgentStructuredQuestion | null;
  summary?: string | null;
  ops?: unknown[] | null;
  message?: string | null;
  code?: string;
  declared_refs?: string[];
  candidate_attempt_id?: string;
  candidate_attempt_number?: number;
  phase_claim_version?: number;
  configuration_node_states?: AgentConfigurationNodeState[];
  continue_draft?: boolean;
  configuration_execution_mode?: 'serial' | 'prepare_then_finalize';
  preparation_status?: 'not_started' | 'running' | 'ready' | 'failed';
  next_configuration_stage?: 'prepare' | 'finalize';
  topology_clarification_id?: string;
  discard_available?: boolean;
  discard_expires_at?: string;
  workflow_graph_revision?: number;
}

export interface AgentValidationError {
  code: string;
  message: string;
  node_id?: string | null;
  graph_index?: number | null;
  /** False for a merely-incomplete field (e.g. a required value left
   * unset) -- those no longer block a plan from landing on canvas, only a
   * genuinely broken graph shape (cycle, multiple parents, etc.) does.
   * See validate_graph_view.py's _STRUCTURAL_CODES. */
  blocking?: boolean;
}

export interface ValidateAgentGraphResponse {
  is_valid: boolean;
  errors: AgentValidationError[];
  candidate_attempt_id?: string;
  candidate_fingerprint?: string;
}

function unwrap<T>(
  response: AxiosResponse<ApiResponse<T>['data']>,
  failureContext: string
): T {
  if (!response.data?.data?.is_success) {
    const errorData = response.data?.data?.data as
      { code?: string } | null | undefined;
    throw new AgentApiError(
      `${failureContext}: ${response.data?.data?.message || 'Unknown error'}`,
      errorData?.code
    );
  }
  return response.data.data.data;
}

export class AgentApiError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
    public readonly status?: number,
    public readonly retryAfterMs?: number
  ) {
    super(message);
    this.name = 'AgentApiError';
  }
}

function normalizeAgentApiError(
  error: unknown,
  fallback: string
): AgentApiError {
  if (error instanceof AgentApiError) return error;
  if (!axios.isAxiosError(error)) {
    return new AgentApiError(error instanceof Error ? error.message : fallback);
  }
  const body = error.response?.data as
    | {
        detail?: { code?: string; message?: string } | string;
        data?: { message?: string; data?: { code?: string } };
      }
    | undefined;
  const detail = body?.detail;
  const code =
    (typeof detail === 'object' ? detail.code : undefined) ??
    body?.data?.data?.code;
  const message =
    (typeof detail === 'object' ? detail.message : detail) ??
    body?.data?.message ??
    fallback;
  const retryAfter = Number(error.response?.headers?.['retry-after']);
  return new AgentApiError(
    message,
    code,
    error.response?.status,
    Number.isFinite(retryAfter) ? Math.max(0, retryAfter * 1000) : undefined
  );
}

async function requestAgent<T>(
  request: () => Promise<AxiosResponse<ApiResponse<T>['data']>>,
  failureContext: string,
  signal?: AbortSignal
): Promise<T> {
  return retryCompatibleWorker(async () => {
    try {
      return unwrap<T>(await request(), failureContext);
    } catch (error) {
      throw normalizeAgentApiError(error, failureContext);
    }
  }, signal);
}

/** Only a server-confirmed, non-mutating preflight can be replayed. */
export async function retryCompatibleWorker<T>(
  request: () => Promise<T>,
  signal?: AbortSignal
): Promise<T> {
  const deadline = Date.now() + 10_000;
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await request();
    } catch (error) {
      if (
        !(error instanceof AgentApiError) ||
        error.status !== 409 ||
        error.code !== 'worker_catalog_incompatible' ||
        attempt >= 2 ||
        Date.now() >= deadline
      )
        throw error;
      await new Promise<void>((resolve, reject) => {
        if (signal?.aborted) {
          reject(new DOMException('Aborted', 'AbortError'));
          return;
        }
        const finish = () => {
          signal?.removeEventListener('abort', abort);
          resolve();
        };
        const timer = setTimeout(
          finish,
          [250, 750][attempt] + Math.floor(Math.random() * 251)
        );
        const abort = () => {
          clearTimeout(timer);
          reject(new DOMException('Aborted', 'AbortError'));
        };
        signal?.addEventListener('abort', abort, { once: true });
      });
    }
  }
}

/** POST /api/logiblocks-agent/start/ — resumes or creates a session for this draft workflow. */
export const startAgentSession = async (
  token: string,
  workflowId: string,
  supportedDraftProtocolVersions: AgentDraftProtocolVersion[] = [1],
  signal?: AbortSignal
): Promise<StartAgentSessionResponse> => {
  const body = {
    workflow_id: workflowId,
    supported_draft_protocol_versions: supportedDraftProtocolVersions,
    client_capabilities: browserAuthoringCapabilities(),
  };
  return requestAgent<StartAgentSessionResponse>(
    () =>
      interceptor.axiosPost(
        'Post',
        APIPaths.logiblocksAgentStart,
        body,
        token,
        signal
      ),
    'Failed to start AI Builder session',
    signal
  );
};

/**
 * POST /api/logiblocks-agent/chat/ — a Gate-2 correction turn, sent after
 * validateAgentGraph() comes back invalid for a proposed plan. Resumes the
 * same conversation rather than starting a new user turn.
 */
export const sendAgentCorrection = async (
  token: string,
  params: {
    sessionId: string;
    workflowId: string;
    serializedData: string;
    correctionErrors: string[];
    draftProtocolVersion?: AgentDraftProtocolVersion;
    workflowGraphRevision?: number;
    expectedSessionStateVersion?: number;
    clientRequestId?: string;
  },
  signal?: AbortSignal
): Promise<AgentChatTurnResponse> => {
  const clientRequestId = params.clientRequestId ?? crypto.randomUUID();
  return requestAgent<AgentChatTurnResponse>(
    () =>
      interceptor.axiosPost(
        'Post',
        APIPaths.logiblocksAgentChat,
        {
          session_id: params.sessionId,
          workflow_id: params.workflowId,
          serialized_data: params.serializedData,
          correction_errors: params.correctionErrors,
          response_version: 2,
          client_request_id: clientRequestId,
          draft_protocol_version: params.draftProtocolVersion,
          workflow_graph_revision: params.workflowGraphRevision,
          expected_session_state_version: params.expectedSessionStateVersion,
        },
        token,
        signal
      ),
    'AI Builder correction failed',
    signal
  );
};

export const sendAgentAnswer = async (
  token: string,
  params: {
    sessionId: string;
    workflowId: string;
    serializedData: string;
    questionId: string;
    values: string[];
    templateChunks?: TextTemplateChunk[];
    draftProtocolVersion?: AgentDraftProtocolVersion;
    workflowGraphRevision?: number;
    expectedSessionStateVersion?: number;
    clientRequestId?: string;
  },
  signal?: AbortSignal
): Promise<AgentChatTurnResponse> => {
  const clientRequestId = params.clientRequestId ?? crypto.randomUUID();
  return requestAgent<AgentChatTurnResponse>(
    () =>
      interceptor.axiosPost(
        'Post',
        APIPaths.logiblocksAgentChat,
        {
          session_id: params.sessionId,
          workflow_id: params.workflowId,
          serialized_data: params.serializedData,
          response_version: 2,
          client_request_id: clientRequestId,
          answer_to_question: {
            question_id: params.questionId,
            values: params.values,
            ...(params.templateChunks
              ? { template_chunks: params.templateChunks }
              : {}),
          },
          draft_protocol_version: params.draftProtocolVersion,
          workflow_graph_revision: params.workflowGraphRevision,
          expected_session_state_version: params.expectedSessionStateVersion,
        },
        token,
        signal
      ),
    'Copilot answer failed',
    signal
  );
};

/** One incremental event from streamAgentMessage's SSE stream. */
export type AgentStreamEvent =
  | { type: 'provider_progress' }
  | { type: 'thinking_delta'; text: string }
  | { type: 'text_delta'; text: string }
  /**
   * Fires the moment the model begins a tool_use block, during generation
   * -- before its input has finished streaming, let alone before the whole
   * turn (and its later 'tool_call' below) resolves. Many turns carry no
   * thinking_delta at all (the model goes straight to tool calls with no
   * verbalized reasoning), so this is often the only progressive signal
   * available while waiting on them.
   */
  | { type: 'tool_use_started'; name: string }
  /** Fires once the whole turn has resolved and this tool is actually
   * about to execute -- a second, later signal for the same tool_use_started
   * name, not a distinct tool. */
  | { type: 'tool_call'; name: string };

/**
 * POST /api/logiblocks-agent/chat_stream/ — a fresh user message, streamed
 * over SSE so the caller can render the model's thinking/text/tool-call
 * progress as it happens instead of waiting on one blocking response.
 *
 * Uses `fetch` directly rather than the shared axios interceptor, since
 * axios cannot read a streaming response body incrementally in the
 * browser (mirrors agentcoreChat.ts's StreamAgentCoreChatMessage — the
 * same pattern already used elsewhere in this app). Resolves with the
 * exact same AgentChatTurnResponse shape sendAgentMessage returns, read
 * from the stream's terminal `done` event — so buildPlanTurn's Gate-2
 * validate/correction loop (still using sendAgentCorrection, unstreamed)
 * needs no changes to consume this function's result.
 *
 * Fresh messages and protocol-v2 configuration continuations go through this
 * endpoint. Corrections still use sendAgentCorrection/`/chat/` (see
 * chat_stream_view.py's module docstring for why correction_errors isn't
 * supported here). A continuation deliberately omits `message` and sends
 * `continue_draft: true`, so it cannot create a fake user turn.
 */
export const streamAgentMessage = async (
  token: string,
  params: {
    sessionId: string;
    workflowId: string;
    serializedData: string;
    message?: string;
    /** v1: one image/PDF attachment per message, sent inline as base64 --
     * see services/attachments.py's module docstring for why (no S3, no
     * separate upload endpoint in v1). */
    attachment?: { filename: string; contentType: string; dataBase64: string };
    clientRequestId?: string;
    draftProtocolVersion?: AgentDraftProtocolVersion;
    workflowGraphRevision?: number;
    continueDraft?: boolean;
    configurationStage?: 'prepare' | 'finalize';
    topologyClarificationId?: string;
    correction?: {
      kind: 'edit_correction';
      draftId: string;
      segmentId: string;
      candidateAttemptId: string;
      phaseClaimVersion: number;
      validationCodes: string[];
    };
  },
  onEvent: (event: AgentStreamEvent) => void,
  signal?: AbortSignal
): Promise<AgentChatTurnResponse> => {
  // Raw fetch, so the axios interceptor's preview guard does not apply. The
  // backend blocks this during preview regardless; this keeps the user-facing
  // message consistent instead of surfacing a bare 403.
  if (guardPreviewMutations()) {
    throw new Error('Read-only preview: write operations are blocked.');
  }
  const request: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      session_id: params.sessionId,
      workflow_id: params.workflowId,
      serialized_data: params.serializedData,
      attachment: params.attachment
        ? {
            filename: params.attachment.filename,
            content_type: params.attachment.contentType,
            data_base64: params.attachment.dataBase64,
          }
        : undefined,
      message: params.message,
      continue_draft: params.continueDraft,
      configuration_stage: params.configurationStage,
      topology_clarification_id: params.topologyClarificationId,
      correction: params.correction
        ? {
            kind: params.correction.kind,
            draft_id: params.correction.draftId,
            segment_id: params.correction.segmentId,
            candidate_attempt_id: params.correction.candidateAttemptId,
            phase_claim_version: params.correction.phaseClaimVersion,
            validation_codes: params.correction.validationCodes,
          }
        : undefined,
      response_version: 2,
      draft_protocol_version: params.draftProtocolVersion,
      supported_configuration_execution_modes: [
        'serial',
        'prepare_then_finalize',
      ],
      workflow_graph_revision: params.workflowGraphRevision,
      client_request_id: params.clientRequestId ?? crypto.randomUUID(),
    }),
    signal,
  };
  const response = await retryCompatibleWorker(async () => {
    const result = await fetch(
      `${baseUrl}${APIPaths.logiblocksAgentChatStream}`,
      request
    );
    if (result.ok) return result;
    const response = result;
    let message = `AI Builder stream request failed with status ${response.status}`;
    let code: string | undefined;
    try {
      const failure = await response.json();
      message = failure?.data?.message || message;
      code = failure?.data?.data?.code;
    } catch {
      // The status remains useful when an intermediary returns a non-JSON body.
    }
    const retryAfter = Number(response.headers?.get('Retry-After'));
    throw new AgentApiError(
      message,
      code,
      response.status,
      Number.isFinite(retryAfter) ? Math.max(0, retryAfter * 1000) : undefined
    );
  }, signal);
  if (!response.body) {
    throw new Error(
      `AI Builder stream request failed with status ${response.status}`
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let outcome: AgentChatTurnResponse | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() ?? '';
    for (const part of parts) {
      if (!part.startsWith('data: ')) continue;
      const parsed = JSON.parse(part.slice('data: '.length));
      if (parsed.type === 'error') {
        throw new AgentApiError(
          parsed.message || 'AI Builder stream failed',
          parsed.code
        );
      }
      if (parsed.type === 'done') {
        outcome = parsed.outcome as AgentChatTurnResponse;
        continue;
      }
      onEvent(parsed as AgentStreamEvent);
    }
  }

  if (!outcome) {
    throw new Error('AI Builder stream ended without a result');
  }
  return outcome;
};

/**
 * POST /api/logiblocks-agent/validate_graph/ — Gate 2 for a candidate graph.
 * `serializedData` here is the CANDIDATE graph (current graph with the
 * proposed plan already folded in via applyGraphOps), never the graph as it
 * stands before the plan is applied.
 */
export const validateAgentGraph = async (
  token: string,
  params: {
    workflowId: string;
    serializedData: string;
    sessionId?: string;
    draftId?: string;
    segmentId?: string;
    candidateAttemptId?: string;
    phaseClaimVersion?: number;
    refMap?: Record<string, string>;
  },
  signal?: AbortSignal
): Promise<ValidateAgentGraphResponse> => {
  return retryCompatibleWorker(async () => {
    try {
      const response = await interceptor.axiosPost(
        'Post',
        APIPaths.logiblocksAgentValidateGraph,
        {
          workflow_id: params.workflowId,
          serialized_data: params.serializedData,
          session_id: params.sessionId,
          draft_id: params.draftId,
          segment_id: params.segmentId,
          candidate_attempt_id: params.candidateAttemptId,
          phase_claim_version: params.phaseClaimVersion,
          ref_map: params.refMap,
        },
        token,
        signal
      );
      return unwrap<ValidateAgentGraphResponse>(
        response,
        'AI Builder graph validation failed'
      );
    } catch (error) {
      throw normalizeAgentApiError(error, 'AI Builder graph validation failed');
    }
  }, signal);
};

export const sendAgentSegmentReceipt = async (
  token: string,
  params: {
    sessionId: string;
    workflowId: string;
    serializedData: string;
    draftId: string;
    segmentId: string;
    workflowGraphRevision: number;
    graphFingerprint: string;
    refMap: Record<string, string>;
    expectedSessionStateVersion: number;
    clientRequestId: string;
    draftProtocolVersion?: AgentDraftProtocolVersion;
    candidateAttemptId?: string;
    phaseClaimVersion?: number;
  },
  signal?: AbortSignal
): Promise<AgentChatTurnResponse> => {
  return requestAgent<AgentChatTurnResponse>(
    () =>
      interceptor.axiosPost(
        'Post',
        APIPaths.logiblocksAgentChat,
        {
          session_id: params.sessionId,
          workflow_id: params.workflowId,
          serialized_data: params.serializedData,
          response_version: 2,
          draft_protocol_version: params.draftProtocolVersion ?? 1,
          client_request_id: params.clientRequestId,
          segment_receipt: {
            draft_id: params.draftId,
            segment_id: params.segmentId,
            workflow_graph_revision: params.workflowGraphRevision,
            graph_fingerprint: params.graphFingerprint,
            ref_map: params.refMap,
            expected_session_state_version: params.expectedSessionStateVersion,
            candidate_attempt_id: params.candidateAttemptId,
            phase_claim_version: params.phaseClaimVersion,
          },
        },
        token,
        signal
      ),
    'Copilot segment receipt failed',
    signal
  );
};

export const cancelAgentDraft = async (
  token: string,
  params: {
    sessionId: string;
    workflowId: string;
    serializedData: string;
    draftId: string;
    clientRequestId: string;
    draftProtocolVersion?: AgentDraftProtocolVersion;
    discard?: boolean;
  },
  signal?: AbortSignal
): Promise<AgentChatTurnResponse> => {
  return requestAgent<AgentChatTurnResponse>(
    () =>
      interceptor.axiosPost(
        'Post',
        APIPaths.logiblocksAgentChat,
        {
          session_id: params.sessionId,
          workflow_id: params.workflowId,
          serialized_data: params.serializedData,
          response_version: 2,
          draft_protocol_version: params.draftProtocolVersion ?? 1,
          client_request_id: params.clientRequestId,
          cancel_draft: { draft_id: params.draftId, discard: params.discard },
        },
        token,
        signal
      ),
    'Failed to stop Copilot drafting',
    signal
  );
};

export interface AgentSessionStatus {
  session_id: string;
  draft_status?: StartAgentSessionResponse['active_draft'] | null;
}

export const listAgentSessions = async (
  token: string,
  workflowId: string,
  signal?: AbortSignal
): Promise<AgentSessionStatus[]> => {
  const query = new URLSearchParams({ workflow_id: workflowId });
  const result = await requestAgent<{ sessions: AgentSessionStatus[] }>(
    () =>
      interceptor.axiosGet(
        'Get',
        `${APIPaths.logiblocksAgentSessions}?${query.toString()}`,
        token,
        undefined,
        signal
      ),
    'Failed to load Copilot recovery status'
  );
  return result.sessions;
};
